import {
  getRandomValueFromArray,
  getTimeDifferenceInHours
} from '@nw/shared/utils';

import {
  ApplicationSectionNameEnums,
  NAT_GUIDE_URL,
  PERSONAL_DETAILS_WITH_SPLIT_SECTIONS,
  USER_PROFILE_DETAILS_FLOW_FIELD
} from '../constants/ApplicationSectionConstants';
import {
  BOOTCAMP_QUOTATION_BANNER_TEXT,
  ENROLLMENT_FEE_QUOTATION_BANNER_TEXT,
  ENROLLMENT_FEE_STAGE_COMPLETE_THRESHOLD
} from '../constants/PaymentFlowCopyConstants';

type SectionTemplateResponseConfig = Record<
  string,
  {
    template_id?: string;
    section_id?: string;
    fieldIdsByName?: Record<string, string>;
  }
>;

type ResolveAb1CurrentEducationValueArgs = {
  initialValues?: Record<string, any> | null;
  sectionTemplateResponseConfig?: SectionTemplateResponseConfig | null;
  templateFieldValuesByFieldId?: Record<string, any> | null;
};

export const OFFLINE_EXAM_SUBMISSION_APPLICATION_TYPE_FIELD_ID =
  '895e914f-c3c1-474a-9638-5e8755c9d3ab';

const getCompletionPercentage = (
  section: any,
  sectionCompletionPercentage: number
) => {
  for (let j = 0; j < section.subSection.length; j++) {
    const subSection = section.subSection[j];
    sectionCompletionPercentage += subSection.completionPercentage;
  }
  return sectionCompletionPercentage;
};

const shouldShowResultInProgressScreenForSlotSelection = (
  subSection?: any,
  isOfflineExamSubmission?: boolean
) => {
  const hasExamCompleted =
    subSection?.sectionCompletedAt && subSection.completionPercentage >= 100;

  if (isOfflineExamSubmission && subSection?.completionPercentage === 50) {
    // Held at 50% until report exists; after user taps post-NAT CTA, API sets
    // firstVisitedDatetime — treat as acknowledged and release the gate.
    return !subSection?.firstVisitedDatetime;
  } else if (isOfflineExamSubmission && hasExamCompleted) {
    return false;
  } else if (hasExamCompleted && subSection?.firstVisitedDatetime) {
    return false;
  } else {
    const timeDifferenceInHours = hasExamCompleted
      ? getTimeDifferenceInHours(subSection.sectionCompletedAt)
      : null;

    return timeDifferenceInHours !== null && timeDifferenceInHours > 0;
  }
};

const shouldShowQualifiedReportPreviewForSlotSelection = (subSection?: any) => {
  const hasExamCompleted =
    subSection?.sectionCompletedAt && subSection.completionPercentage >= 100;
  if (!hasExamCompleted || subSection?.firstVisitedDatetime) return false;
  const timeDifferenceInHours = getTimeDifferenceInHours(
    subSection.sectionCompletedAt
  );
  return (
    timeDifferenceInHours !== null &&
    timeDifferenceInHours <= 0 &&
    timeDifferenceInHours > -24
  );
};

export const isOfflineExamSubmissionApplication = (
  applicationType?: string,
  templateApplicationType?: string
) =>
  applicationType === 'OFFLINE_EXAM_SUBMISSION' ||
  templateApplicationType === 'OFFLINE_EXAM_SUBMISSION';

export const isDirectCampusVisitedApplication = (
  initialValues?: Record<string, unknown> | null
): boolean => initialValues?.IS_DIRECT_CAMPUS_VISITED === 'YES';

export const isPersonalDetailsWithSplitSectionsFlow = (
  userProfileDetailsFlow?: string | null
): boolean => userProfileDetailsFlow === PERSONAL_DETAILS_WITH_SPLIT_SECTIONS;

export const getActivePersonalDetailsSectionId = (
  initialValues?: Record<string, unknown> | null
): string =>
  isPersonalDetailsWithSplitSectionsFlow(
    initialValues?.[USER_PROFILE_DETAILS_FLOW_FIELD] as string | undefined
  )
    ? ApplicationSectionNameEnums.PERSONAL_DETAILS_AB1
    : ApplicationSectionNameEnums.PERSONAL_DETAILS;

export const resolveAb1CurrentEducationValue = ({
  initialValues,
  sectionTemplateResponseConfig,
  templateFieldValuesByFieldId
}: ResolveAb1CurrentEducationValueArgs): string => {
  const ab1FieldIds =
    sectionTemplateResponseConfig?.[
      ApplicationSectionNameEnums.PERSONAL_DETAILS_AB1
    ]?.fieldIdsByName;

  const whoIsFillingFieldId = ab1FieldIds?.['WHO_IS_FILLING'];
  const whoIsFillingFromFieldId = whoIsFillingFieldId
    ? templateFieldValuesByFieldId?.[whoIsFillingFieldId]
    : undefined;
  const whoIsFilling =
    (whoIsFillingFromFieldId !== undefined &&
      whoIsFillingFromFieldId !== null &&
      String(whoIsFillingFromFieldId).trim() !== ''
      ? whoIsFillingFromFieldId
      : initialValues?.['WHO_IS_FILLING']) ?? '';

  const educationFieldName =
    whoIsFilling === 'PARENT'
      ? 'STUDENT_CURRENT_EDUCATION'
      : 'CURRENT_EDUCATION';

  const educationFromInitialValues = initialValues?.[educationFieldName];
  if (
    educationFromInitialValues !== undefined &&
    educationFromInitialValues !== null &&
    String(educationFromInitialValues).trim() !== ''
  ) {
    return String(educationFromInitialValues);
  }

  const educationFieldId = ab1FieldIds?.[educationFieldName];
  const educationFromFieldId = educationFieldId
    ? templateFieldValuesByFieldId?.[educationFieldId]
    : undefined;
  if (
    educationFromFieldId !== undefined &&
    educationFromFieldId !== null &&
    String(educationFromFieldId).trim() !== ''
  ) {
    return String(educationFromFieldId);
  }

  return '';
};

type FieldsTemplateResponseConfigEntry = {
  template_id?: string;
  section_id?: string;
  field_id?: string;
};

type BuildWhoIsFillingStudentHandoffRequestArgs = {
  applicationId: string;
  initialValues?: Record<string, any> | null;
  valuesSource?: Record<string, any> | null;
  sectionTemplateResponseConfig?: SectionTemplateResponseConfig | null;
  fieldsTemplateResponseConfig?: Record<
    string,
    FieldsTemplateResponseConfigEntry | undefined
  > | null;
};

type WhoIsFillingStudentHandoffRequest = {
  application_id: string;
  template_id: string;
  section_id: string;
  field_responses: Array<{ field_id: string; field_value: string }>;
};

const WHO_IS_FILLING_HANDOFF_CANDIDATES: Array<{
  fieldName: string;
  getValue: (valuesSource?: Record<string, any> | null) => string;
}> = [
    {
      fieldName: 'WHO_IS_FILLING',
      getValue: () => 'STUDENT'
    },
    {
      fieldName: 'CURRENT_EDUCATION',
      getValue: (valuesSource) => valuesSource?.STUDENT_CURRENT_EDUCATION ?? ''
    },
    {
      fieldName: 'ACADEMIC_STREAM',
      getValue: (valuesSource) => valuesSource?.STUDENT_ACADEMIC_STREAM ?? ''
    },
    {
      fieldName: 'CLASS_XII_BOARD',
      getValue: (valuesSource) => valuesSource?.STUDENT_CLASS_XII_BOARD ?? ''
    }
  ];


export const buildWhoIsFillingStudentHandoffRequest = ({
  applicationId,
  initialValues,
  valuesSource,
  sectionTemplateResponseConfig,
  fieldsTemplateResponseConfig
}: BuildWhoIsFillingStudentHandoffRequestArgs): WhoIsFillingStudentHandoffRequest | null => {
  const activeSectionId = getActivePersonalDetailsSectionId(initialValues);
  const sectionConfig = sectionTemplateResponseConfig?.[activeSectionId];
  const whoIsFillingGlobalConfig =
    fieldsTemplateResponseConfig?.['WHO_IS_FILLING'];

  const templateId =
    sectionConfig?.template_id ?? whoIsFillingGlobalConfig?.template_id;
  const sectionId =
    sectionConfig?.section_id ?? whoIsFillingGlobalConfig?.section_id;

  if (!templateId || !sectionId) {
    return null;
  }

  const fieldIdsByName = sectionConfig?.fieldIdsByName ?? {};
  const fieldResponses: Array<{ field_id: string; field_value: string }> = [];

  WHO_IS_FILLING_HANDOFF_CANDIDATES.forEach(({ fieldName, getValue }) => {
    const fieldId = fieldIdsByName[fieldName];
    if (typeof fieldId === 'string' && fieldId.trim() !== '') {
      fieldResponses.push({
        field_id: fieldId,
        field_value: getValue(valuesSource)
      });
    }
  });

  if (fieldResponses.length === 0) {
    return null;
  }

  return {
    application_id: applicationId,
    template_id: templateId,
    section_id: sectionId,
    field_responses: fieldResponses
  };
};

export const isSectionCompleted = (section?: any): boolean =>
  Boolean(section && section.completionPercentage === 100);

const PAYMENT_FLOW_SECTION_IDS = new Set([
  ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026,
  ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026,
  ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026,
  ApplicationSectionNameEnums.NIAT_BOOTCAMP_FEE_2026
]);

/** Enrollment fee paid or section completion reached the bootcamp unlock threshold. */
export const isEnrollmentFeeStageComplete = (
  sectionDetailsInfoTabsConfig: Array<{ id: string; completionPercentage?: number }>,
  enrollmentSection?: { completionPercentage?: number }
): boolean => {
  const enrollmentSectionFromConfig =
    enrollmentSection ??
    sectionDetailsInfoTabsConfig.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
    );

  const completionPercentage =
    enrollmentSectionFromConfig?.completionPercentage ?? 0;

  return completionPercentage >= ENROLLMENT_FEE_STAGE_COMPLETE_THRESHOLD;
};

/** Hide desktop sidebar and mobile progress bar on focused ER / Bootcamp flows. */
export const shouldHidePaymentFlowNavigation = (
  currentSelectedSectionTabId?: string,
  currentSelectedSubSectionId?: string
): boolean => {
  const activeSectionId =
    currentSelectedSubSectionId || currentSelectedSectionTabId;

  return Boolean(
    activeSectionId && PAYMENT_FLOW_SECTION_IDS.has(activeSectionId)
  );
};

/** First incomplete bootcamp sub-section; defaults to registration. */
export const getDefaultBootcampSubSectionId = (
  bootcampSection?: {
    subSection?: Array<{ id: string; completionPercentage?: number }>;
  }
): string => {
  const subSections = bootcampSection?.subSection ?? [];
  const incompleteSubSection = subSections.find(
    (subSection) => (subSection.completionPercentage ?? 0) < 100
  );

  if (incompleteSubSection?.id) {
    return incompleteSubSection.id;
  }

  return (
    subSections.find(
      (subSection) =>
        subSection.id ===
        ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026
    )?.id ??
    subSections[0]?.id ??
    ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026
  );
};

/** Seat reservation (take admission) complete without direct campus visit flows — hide non-whitelisted tabs. */
export const getShouldHideSectionOnCompletionOfSR = (
  sectionDetailsInfoTabsConfig: any[]
): boolean => {
  const takeAdmissionSectionInConfig = sectionDetailsInfoTabsConfig.find(
    (s) =>
      s.id === ApplicationSectionNameEnums.TAKE_ADMISSION ||
      s.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
  );
  const takeAdmissionCompletionPercentage =
    takeAdmissionSectionInConfig?.completionPercentage ?? 0;
  const hasDirectCampusVisitSection = Boolean(
    sectionDetailsInfoTabsConfig.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026
    )
  );
  const hasDirectCampusVisitSrSection = Boolean(
    sectionDetailsInfoTabsConfig.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
    )
  );
  return (
    takeAdmissionCompletionPercentage >= 100 &&
    !hasDirectCampusVisitSection &&
    !hasDirectCampusVisitSrSection
  );
};

/** Hide enrollment fee until upskilling registration is complete; then hide upskilling registration. */
export const applyUpskillingAndEnrollmentSidebarVisibility = (
  sectionDetailsInfoTabsDetailsArray: any[]
): void => {
  const enrollmentSection = sectionDetailsInfoTabsDetailsArray.find(
    (section) =>
      section.id === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
  );
  if (!enrollmentSection) {
    return;
  }

  const takeAdmissionSection = sectionDetailsInfoTabsDetailsArray.find(
    (section) => section.id === ApplicationSectionNameEnums.TAKE_ADMISSION
  );
  const directCampusSrSection = sectionDetailsInfoTabsDetailsArray.find(
    (section) =>
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
  );

  const isSeatReservationComplete =
    (takeAdmissionSection?.completionPercentage ?? 0) >= 100 ||
    (directCampusSrSection?.completionPercentage ?? 0) >= 100;

  if (!isSeatReservationComplete) {
    enrollmentSection.isHidden = true;
    return;
  }

  if (takeAdmissionSection) {
    takeAdmissionSection.isHidden = true;
  }
  if (directCampusSrSection) {
    directCampusSrSection.isHidden = true;
  }

  const bootcampSection = sectionDetailsInfoTabsDetailsArray.find(
    (section) => section.id === ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026
  );
  const isEnrollmentStageComplete = isEnrollmentFeeStageComplete(
    sectionDetailsInfoTabsDetailsArray,
    enrollmentSection
  );

  if (isEnrollmentStageComplete) {
    enrollmentSection.isHidden = true;
    if (bootcampSection && !bootcampSection.isDisable) {
      bootcampSection.isHidden = false;
    }
    return;
  }

  if (!enrollmentSection.isDisable) {
    enrollmentSection.isHidden = false;
  }
  if (bootcampSection) {
    bootcampSection.isHidden = true;
  }
};

export const getSectionDetailsDisableInfoTabsConfig = (
  sectionDetailsInfoTabsConfig: any,
  loggedInUserRole?: string,
  isRescheduleExam?: boolean,
  applicationType?: string,
  templateApplicationType?: string,
  isDirectCampusVisitedFromField = false,
  userProfileDetailsFlow?: string
) => {
  const isOfflineExamSubmission = isOfflineExamSubmissionApplication(
    applicationType,
    templateApplicationType
  );

  // Split personal-details flow (Variant B): the pre-payment profile is the
  // minimal AB1 section and an additional-details section is completed after
  // payment. Resolved only from the sticky field, never live Unleash.
  const isSplitSectionsFlow =
    isPersonalDetailsWithSplitSectionsFlow(userProfileDetailsFlow);
  const activePersonalDetailsSectionId = isSplitSectionsFlow
    ? ApplicationSectionNameEnums.PERSONAL_DETAILS_AB1
    : ApplicationSectionNameEnums.PERSONAL_DETAILS;
  const additionalDetailsSectionInConfig = sectionDetailsInfoTabsConfig.find(
    (section) =>
      section.id === ApplicationSectionNameEnums.STUDENT_ADDITIONAL_DETAILS
  );
  // Precomputed so the NAT gate is correct regardless of section ordering.
  let additionalDetailsCompletionPercentage =
    additionalDetailsSectionInConfig?.completionPercentage ?? 0;

  const sectionDetailsInfoTabsDetailsArray: any[] = [];

  let selectedSectionTabId = '';
  let selectedSubSectionId = '';
  let selectedSectionCompletionPercentage = 0;

  //maintain completion percentage of each section
  let bookExamSlotCompletionPercentage = 0;
  let campusVisitSchedulerCompletionPercentage = 0;
  let personalDetailsCompletionPercentage = 0;
  let webinarSectionCompletionPercentage = 0;
  let onlineCounsellingCompletionPercentage = 0;
  let bookAdmissionTestFeeCompletionPercentage = 0;
  let natSlotSelectionCompletionPercentage = 0;
  let enrollmentCompletionPercentage = 0;
  const takeAdmissionSectionInConfig = sectionDetailsInfoTabsConfig.find(
    (s) =>
      s.id === ApplicationSectionNameEnums.TAKE_ADMISSION ||
      s.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
  );
  let takeAdmissionCompletionPercentage =
    takeAdmissionSectionInConfig?.completionPercentage ?? 0;
  let closureSessionCompletionPercentage = 0;
  let natOfficeVisitedCompletionPercentage = 0;
  const directCampusVisitSection = sectionDetailsInfoTabsConfig.find(
    (section) =>
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026
  );
  const hasDirectCampusVisitSection = Boolean(directCampusVisitSection);
  const directCampusVisitSrSection = sectionDetailsInfoTabsConfig.find(
    (section) =>
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
  );
  const hasDirectCampusVisitSrSection = Boolean(directCampusVisitSrSection);
  const isDirectFlowUser =
    isDirectCampusVisitedFromField || hasDirectCampusVisitSrSection;
  /** NAT qualified but report not available yet (offline 50% gate, retake, or online wait window). */
  let isNatQualifiedReportPendingFlow = false;
  let directCampusVisitCompletionPercentage =
    directCampusVisitSection?.completionPercentage ?? 0;
  let directCampusVisitSrCompletionPercentage =
    directCampusVisitSrSection?.completionPercentage ?? 0;

  const getPrimaryDirectCampusVisitTabIdForSelection = () => {
    if (hasDirectCampusVisitSection) {
      return ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026;
    }
    if (isDirectCampusVisitedFromField && hasDirectCampusVisitSrSection) {
      return ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026;
    }
    return ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026;
  };

  const primaryDirectCampusVisitTabId =
    getPrimaryDirectCampusVisitTabIdForSelection();

  const visibleSectionsOnCompletionOfSR = [
    ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026,
    ApplicationSectionNameEnums.TAKE_ADMISSION,
    ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026,
    ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026
  ];
  const postAdmissionHiddenSections = [
    ApplicationSectionNameEnums.NAT_SLOT_SELECTION
  ];

  const applySectionVisibilityOverrides = ({
    visibleSections = [],
    hiddenSections = []
  }: {
    visibleSections?: string[];
    hiddenSections?: string[];
  }) => {
    const visibleSectionsSet = new Set(visibleSections);
    const hiddenSectionsSet = new Set(hiddenSections);
    const hasVisibleOverrides = visibleSectionsSet.size > 0;

    sectionDetailsInfoTabsDetailsArray.forEach((section) => {
      if (hasVisibleOverrides) {
        section.isHidden = !visibleSectionsSet.has(section.id);
        return;
      }

      if (hiddenSectionsSet.has(section.id)) section.isHidden = true;
    });
  };

  for (let i = 0; i < sectionDetailsInfoTabsConfig.length; i++) {
    const section = sectionDetailsInfoTabsConfig[i];

    // Variant B hides the legacy Personal Details section; Variant A (and any
    // non-split flow) hides the split sections if the backend returned them.
    // Always set both ways because tab config objects are reused across
    // recomputes and would otherwise keep a stale isHidden.
    if (isSplitSectionsFlow) {
      if (section.id === ApplicationSectionNameEnums.PERSONAL_DETAILS) {
        section.isHidden = true;
        section.isDisable = true;
        sectionDetailsInfoTabsDetailsArray.push(section);
        continue;
      }
    } else if (
      section.id === ApplicationSectionNameEnums.PERSONAL_DETAILS_AB1 ||
      section.id === ApplicationSectionNameEnums.STUDENT_ADDITIONAL_DETAILS
    ) {
      section.isHidden = true;
      section.isDisable = true;
      sectionDetailsInfoTabsDetailsArray.push(section);
      continue;
    }

    //handle personal details section (AB1 minimal section for the split flow)
    if (section.id === activePersonalDetailsSectionId) {
      if (isSplitSectionsFlow) {
        // Clear any hidden flag left behind by a recompute that ran before the
        // sticky flow value had loaded.
        section.isHidden = false;
      }
      //this is initial section, so it is not disable
      section.isDisable = false;
      personalDetailsCompletionPercentage = section.completionPercentage;

      let natCompletionPercentage = 0;
      const natSection = sectionDetailsInfoTabsConfig.find(
        (section) => section.id === ApplicationSectionNameEnums.NAT
      );
      natCompletionPercentage = natSection
        ? getCompletionPercentage(natSection, 0)
        : 0;
      natSlotSelectionCompletionPercentage = natCompletionPercentage;

      if (
        (section.completionPercentage < 100 && natCompletionPercentage < 100) ||
        !loggedInUserRole
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
      }
    } else if (
      section.id === ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
    ) {
      bookAdmissionTestFeeCompletionPercentage = section.completionPercentage;

      if (
        personalDetailsCompletionPercentage < 100 &&
        natSlotSelectionCompletionPercentage < 100
      ) {
        section.isDisable = true;
        selectedSectionTabId = activePersonalDetailsSectionId;
        selectedSectionCompletionPercentage =
          personalDetailsCompletionPercentage;
      } else if (
        hasDirectCampusVisitSection &&
        directCampusVisitCompletionPercentage < 100
      ) {
        section.isDisable = true;
        if (!selectedSectionTabId && !selectedSubSectionId) {
          selectedSectionTabId = primaryDirectCampusVisitTabId;
          selectedSectionCompletionPercentage =
            directCampusVisitCompletionPercentage;
        }
      } else if (
        hasDirectCampusVisitSrSection &&
        directCampusVisitSrCompletionPercentage < 100
      ) {
        section.isDisable = true;
        if (!selectedSectionTabId && !selectedSubSectionId) {
          selectedSectionTabId =
            ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026;
          selectedSectionCompletionPercentage =
            directCampusVisitSrCompletionPercentage;
        }
      } else if (
        personalDetailsCompletionPercentage >= 100 &&
        loggedInUserRole
      ) {
        section.isDisable = false;
        if (
          !selectedSectionTabId &&
          !selectedSubSectionId &&
          bookAdmissionTestFeeCompletionPercentage < 100
        ) {
          selectedSectionTabId =
            ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE;
          selectedSectionCompletionPercentage = section.completionPercentage;
        }
      }
    } else if (
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026
    ) {
      directCampusVisitCompletionPercentage = section.completionPercentage ?? 0;

      if (
        personalDetailsCompletionPercentage < 100 &&
        natSlotSelectionCompletionPercentage < 100
      ) {
        section.isDisable = true;
        selectedSectionTabId = activePersonalDetailsSectionId;
        selectedSectionCompletionPercentage =
          personalDetailsCompletionPercentage;
      } else if (
        personalDetailsCompletionPercentage >= 100 &&
        loggedInUserRole
      ) {
        section.isDisable = false;

        if (
          !selectedSectionTabId &&
          !selectedSubSectionId &&
          directCampusVisitCompletionPercentage < 100
        ) {
          selectedSectionTabId =
            ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026;
          selectedSectionCompletionPercentage =
            directCampusVisitCompletionPercentage;
        }
      }
    } else if (
      section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
    ) {
      directCampusVisitSrCompletionPercentage =
        section.completionPercentage ?? 0;

      // No DIRECT_CAMPUS_VISIT_2026 in config: show SR only for direct-campus (template) users.
      // Always set both ways — tab config objects are reused across recomputes; leaving isHidden
      // unset would keep stale true after IS_DIRECT_CAMPUS_VISITED loads from template.
      if (!hasDirectCampusVisitSection) {
        section.isHidden = !isDirectCampusVisitedFromField;
      }

      if (
        personalDetailsCompletionPercentage < 100 &&
        natSlotSelectionCompletionPercentage < 100
      ) {
        section.isDisable = true;
        selectedSectionTabId = activePersonalDetailsSectionId;
        selectedSectionCompletionPercentage =
          personalDetailsCompletionPercentage;
      } else if (
        !(
          !hasDirectCampusVisitSection ||
          directCampusVisitCompletionPercentage >= 100
        )
      ) {
        section.isDisable = true;
      } else if (
        personalDetailsCompletionPercentage >= 100 &&
        loggedInUserRole
      ) {
        section.isDisable = false;
      }

      if (
        !section.isHidden &&
        !section.isDisable &&
        directCampusVisitSrCompletionPercentage < 100 &&
        !selectedSubSectionId &&
        !selectedSectionTabId
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
      }
    } else if (
      section.id === ApplicationSectionNameEnums.BOOK_EXAM_SLOT_AND_PAY
    ) {
      //handle book exam slot and pay section
      for (let j = 0; j < section.subSection.length; j++) {
        const subSection = section.subSection[j];
        bookExamSlotCompletionPercentage += subSection.completionPercentage;

        if (
          !selectedSubSectionId &&
          !selectedSectionTabId &&
          subSection.completionPercentage < 100
        ) {
          selectedSubSectionId = subSection.id;
        }
      }

      bookExamSlotCompletionPercentage = bookExamSlotCompletionPercentage / 2;
      section.completionPercentage = bookExamSlotCompletionPercentage;

      if (
        (bookExamSlotCompletionPercentage > 0 || loggedInUserRole) &&
        personalDetailsCompletionPercentage >= 100
      ) {
        //loggedInUserRole case will be checked when personal details section is 100% but user has not clicked on save and book . so we should not move to payment section on next reload.
        section.isDisable = false;
      } else if (personalDetailsCompletionPercentage < 100) {
        section.isDisable = true;
      }

      if (
        !selectedSectionTabId &&
        bookExamSlotCompletionPercentage < 100 &&
        personalDetailsCompletionPercentage >= 100
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = bookExamSlotCompletionPercentage;
      }
    } else if (
      section.id === ApplicationSectionNameEnums.STUDENT_ADDITIONAL_DETAILS
    ) {
      // Post-payment additional details (split flow): unlocks once the fee is
      // paid and gates NAT slot selection. Clear any hidden flag left behind by
      // a recompute that ran before the sticky flow value had loaded.
      section.isHidden = false;
      additionalDetailsCompletionPercentage = section.completionPercentage;

      if (bookAdmissionTestFeeCompletionPercentage >= 100) {
        section.isDisable = false;
        if (
          !selectedSectionTabId &&
          !selectedSubSectionId &&
          additionalDetailsCompletionPercentage < 100
        ) {
          selectedSectionTabId = section.id;
          selectedSectionCompletionPercentage =
            additionalDetailsCompletionPercentage;
        }
      } else {
        section.isDisable = true;
      }
    } else if (section.id === ApplicationSectionNameEnums.NAT_SLOT_SELECTION) {
      const natSlotSelectionSubSections = section.subSection || [];
      natSlotSelectionCompletionPercentage = getCompletionPercentage(
        section,
        0
      );
      const examSlotDetailsSubSection = natSlotSelectionSubSections.find(
        (eachSubSection) =>
          eachSubSection.id === ApplicationSectionNameEnums.EXAM_SLOT_DETAILS
      );
      const topinExamSubSection = natSlotSelectionSubSections.find(
        (eachSubSection) =>
          eachSubSection.id === ApplicationSectionNameEnums.TOPIN_EXAM
      );

      const retakeExamSlotDetailsSubSection = natSlotSelectionSubSections.find(
        (eachSubSection) =>
          eachSubSection.id ===
          ApplicationSectionNameEnums.RETAKE_EXAM_SLOT_DETAILS
      );

      const retakeTopinExamSubSection = natSlotSelectionSubSections.find(
        (eachSubSection) =>
          eachSubSection.id === ApplicationSectionNameEnums.RETAKE_TOPIN_EXAM
      );

      // In the split flow, NAT additionally requires the post-payment
      // additional-details section to be complete before it unlocks.
      const isProfileStageCompleteForNat =
        personalDetailsCompletionPercentage >= 100 &&
        (!isSplitSectionsFlow || additionalDetailsCompletionPercentage >= 100);

      if (
        bookAdmissionTestFeeCompletionPercentage >= 100 &&
        isProfileStageCompleteForNat
      ) {
        section.isDisable = false;
      } else if (
        bookAdmissionTestFeeCompletionPercentage < 100 &&
        personalDetailsCompletionPercentage >= 100 &&
        loggedInUserRole
      ) {
        section.isDisable = true;
        if (
          hasDirectCampusVisitSection &&
          directCampusVisitCompletionPercentage < 100
        ) {
          selectedSectionTabId = primaryDirectCampusVisitTabId;
          selectedSectionCompletionPercentage =
            directCampusVisitCompletionPercentage;
        } else if (
          hasDirectCampusVisitSrSection &&
          directCampusVisitSrCompletionPercentage < 100
        ) {
          selectedSectionTabId =
            ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026;
          selectedSectionCompletionPercentage =
            directCampusVisitSrCompletionPercentage;
        } else {
          selectedSectionTabId =
            ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE;
          selectedSectionCompletionPercentage =
            bookAdmissionTestFeeCompletionPercentage;
        }
      } else if (personalDetailsCompletionPercentage < 100) {
        if (
          natSlotSelectionCompletionPercentage /
          natSlotSelectionSubSections.length <=
          100 &&
          ((topinExamSubSection &&
            shouldShowResultInProgressScreenForSlotSelection(
              topinExamSubSection,
              isOfflineExamSubmission
            )) ||
            (retakeTopinExamSubSection &&
              shouldShowResultInProgressScreenForSlotSelection(
                retakeTopinExamSubSection,
                isOfflineExamSubmission
              )))
        ) {
          section.isDisable = true;
          selectedSectionTabId = activePersonalDetailsSectionId;
          selectedSectionCompletionPercentage =
            personalDetailsCompletionPercentage;
        } else if (
          personalDetailsCompletionPercentage < 100 &&
          isOfflineExamSubmission
        ) {
          section.isDisable = true;
          selectedSectionTabId = activePersonalDetailsSectionId;
          selectedSectionCompletionPercentage =
            personalDetailsCompletionPercentage;
        } else if (
          natSlotSelectionCompletionPercentage /
          natSlotSelectionSubSections.length >=
          100
        ) {
          section.isDisable = false;
        } else {
          section.isDisable = true;
          selectedSectionTabId = activePersonalDetailsSectionId;
          selectedSectionCompletionPercentage =
            personalDetailsCompletionPercentage;
        }
      }

      if (
        retakeTopinExamSubSection &&
        retakeTopinExamSubSection.completionPercentage >= 100
      ) {
        if (natSlotSelectionSubSections.length === 1) {
          natSlotSelectionCompletionPercentage = 100;
        } else if (
          shouldShowResultInProgressScreenForSlotSelection(
            retakeTopinExamSubSection,
            isOfflineExamSubmission
          )
        ) {
          section.shouldShowResultInProgressScreen = true;
          natSlotSelectionCompletionPercentage = 50;
        } else if (
          shouldShowQualifiedReportPreviewForSlotSelection(
            retakeTopinExamSubSection
          )
        ) {
          section.shouldShowQualifiedReportPreview = true;
          natSlotSelectionCompletionPercentage = 50;
        } else {
          natSlotSelectionCompletionPercentage =
            natSlotSelectionCompletionPercentage /
            natSlotSelectionSubSections.length;
        }
      } else if (
        topinExamSubSection &&
        topinExamSubSection.completionPercentage >= 100 &&
        natSlotSelectionSubSections.length === 1
      ) {
        natSlotSelectionCompletionPercentage = 100;
      } else if (
        topinExamSubSection &&
        shouldShowResultInProgressScreenForSlotSelection(
          topinExamSubSection,
          isOfflineExamSubmission
        )
      ) {
        section.shouldShowResultInProgressScreen = true;
        natSlotSelectionCompletionPercentage = 50;
      } else if (
        topinExamSubSection &&
        shouldShowQualifiedReportPreviewForSlotSelection(topinExamSubSection)
      ) {
        section.shouldShowQualifiedReportPreview = true;
        natSlotSelectionCompletionPercentage = 50;
      } else if (
        isOfflineExamSubmission &&
        ((topinExamSubSection?.completionPercentage === 50 &&
          topinExamSubSection?.firstVisitedDatetime) ||
          (retakeTopinExamSubSection?.completionPercentage === 50 &&
            retakeTopinExamSubSection?.firstVisitedDatetime))
      ) {
        // Post-NAT CTA completed while still at 50%: complete NAT so post-NAT sections unlock.
        natSlotSelectionCompletionPercentage = 100;
      } else {
        natSlotSelectionCompletionPercentage =
          natSlotSelectionCompletionPercentage /
          natSlotSelectionSubSections.length;
      }
      section.completionPercentage = natSlotSelectionCompletionPercentage;

      const shouldSelectNatSlotSelection =
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        section.completionPercentage < 100 &&
        (!isSplitSectionsFlow ||
          additionalDetailsCompletionPercentage >= 100);

      if (shouldSelectNatSlotSelection) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;

        // If retake exam slot details is 100% complete and retake topin exam exists, select retake topin exam
        if (
          retakeExamSlotDetailsSubSection &&
          retakeExamSlotDetailsSubSection.completionPercentage >= 100 &&
          retakeTopinExamSubSection
        ) {
          selectedSubSectionId = retakeTopinExamSubSection.id;
        }
        // If exam slot details is 100% complete and retake exam slot exists (but not 100%), select retake exam slot
        else if (
          examSlotDetailsSubSection &&
          examSlotDetailsSubSection.completionPercentage >= 100 &&
          retakeExamSlotDetailsSubSection &&
          retakeExamSlotDetailsSubSection.completionPercentage < 100
        ) {
          selectedSubSectionId = retakeExamSlotDetailsSubSection.id;
        }
        // If exam slot details is 100% complete and topin exam exists (initial structure), select topin exam
        else if (
          examSlotDetailsSubSection &&
          examSlotDetailsSubSection.completionPercentage >= 100 &&
          topinExamSubSection
        ) {
          selectedSubSectionId = topinExamSubSection.id;
        }
        // If exam slot details exists, select it
        else if (examSlotDetailsSubSection) {
          selectedSubSectionId = examSlotDetailsSubSection.id;
        }
        // Fallback to first subsection
        else if (natSlotSelectionSubSections.length > 0) {
          selectedSubSectionId = natSlotSelectionSubSections[0].id;
        }
      }
      const isOfflineExamTopinAwaitingReport =
        isOfflineExamSubmission &&
        ((topinExamSubSection?.completionPercentage === 50 &&
          examSlotDetailsSubSection &&
          examSlotDetailsSubSection.completionPercentage >= 100) ||
          (retakeTopinExamSubSection?.completionPercentage === 50 &&
            retakeExamSlotDetailsSubSection &&
            retakeExamSlotDetailsSubSection.completionPercentage >= 100));

      isNatQualifiedReportPendingFlow =
        Boolean(section.shouldShowResultInProgressScreen) ||
        Boolean(isOfflineExamTopinAwaitingReport);
    } else if (
      section.id === ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT
    ) {
      campusVisitSchedulerCompletionPercentage = getCompletionPercentage(
        section,
        0
      );

      campusVisitSchedulerCompletionPercentage =
        campusVisitSchedulerCompletionPercentage / section.subSection.length;

      section.completionPercentage = campusVisitSchedulerCompletionPercentage;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else {
        section.isHidden = false;
        section.isDisable = false;
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        campusVisitSchedulerCompletionPercentage < 100
      ) {
        if (campusVisitSchedulerCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSubSectionId =
            ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER;
          selectedSectionCompletionPercentage =
            campusVisitSchedulerCompletionPercentage;
        } else {
          selectedSectionTabId = ApplicationSectionNameEnums.NAT;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (section.id === ApplicationSectionNameEnums.WEBINAR) {
      webinarSectionCompletionPercentage =
        getCompletionPercentage(section, 0) / section.subSection.length;

      section.completionPercentage = webinarSectionCompletionPercentage;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else {
        section.isHidden = false;
        section.isDisable = false;
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        webinarSectionCompletionPercentage < 100
      ) {
        if (webinarSectionCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSubSectionId =
            ApplicationSectionNameEnums.WEBINAR_REGISTRATION;
          selectedSectionCompletionPercentage =
            webinarSectionCompletionPercentage;
        } else {
          selectedSectionTabId = ApplicationSectionNameEnums.NAT;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (section.id === ApplicationSectionNameEnums.ONLINE_COUNSELLING) {
      onlineCounsellingCompletionPercentage = getCompletionPercentage(
        section,
        0
      );
      onlineCounsellingCompletionPercentage =
        onlineCounsellingCompletionPercentage / section.subSection.length;

      section.completionPercentage = onlineCounsellingCompletionPercentage;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else {
        section.isHidden = false;
        if (
          webinarSectionCompletionPercentage >= 100 ||
          section.completionPercentage >= 100
        ) {
          section.isDisable = false;
        } else {
          section.isDisable = true;
        }
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        onlineCounsellingCompletionPercentage < 100
      ) {
        if (onlineCounsellingCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSubSectionId =
            ApplicationSectionNameEnums.BOOK_A_PRE_COUNSELLING_SESSION;
          selectedSectionCompletionPercentage =
            onlineCounsellingCompletionPercentage;
        } else {
          selectedSectionTabId = ApplicationSectionNameEnums.WEBINAR;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (
      section.id === ApplicationSectionNameEnums.CLOSURE_SESSION_REGISTRATION
    ) {
      closureSessionCompletionPercentage = section.completionPercentage ?? 0;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else {
        section.isHidden = false;
        if (onlineCounsellingCompletionPercentage >= 100) {
          section.isDisable = false;
        } else {
          section.isDisable = true;
        }
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        closureSessionCompletionPercentage < 100
      ) {
        if (closureSessionCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSectionCompletionPercentage =
            closureSessionCompletionPercentage;
        } else {
          selectedSectionTabId = ApplicationSectionNameEnums.ONLINE_COUNSELLING;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (section.id === ApplicationSectionNameEnums.NAT_OFFICE_VISITED) {
      natOfficeVisitedCompletionPercentage = section.completionPercentage ?? 0;

      if (natSlotSelectionCompletionPercentage < 100) {
        section.isHidden = true;
      } else {
        section.isHidden = false;
        if (
          onlineCounsellingCompletionPercentage >= 100 ||
          campusVisitSchedulerCompletionPercentage >= 100
        ) {
          section.isDisable = false;
        } else {
          section.isDisable = true;
        }
      }

      if (
        !selectedSectionTabId &&
        !selectedSubSectionId &&
        natOfficeVisitedCompletionPercentage < 100
      ) {
        if (natOfficeVisitedCompletionPercentage > 0) {
          selectedSectionTabId = section.id;
          selectedSectionCompletionPercentage =
            natOfficeVisitedCompletionPercentage;
        } else {
          selectedSectionTabId =
            campusVisitSchedulerCompletionPercentage >= 100
              ? ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT
              : ApplicationSectionNameEnums.ONLINE_COUNSELLING;
          selectedSectionCompletionPercentage = 100;
        }
      }
    } else if (section.id === ApplicationSectionNameEnums.TAKE_ADMISSION) {
      takeAdmissionCompletionPercentage = section.completionPercentage;
      const isDirectCampusPathClearForTakeAdmission =
        hasDirectCampusVisitSection
          ? directCampusVisitCompletionPercentage >= 100
          : isDirectCampusVisitedFromField
            ? directCampusVisitSrCompletionPercentage >= 100
            : directCampusVisitCompletionPercentage >= 100;
      if (
        natSlotSelectionCompletionPercentage < 100 &&
        section.completionPercentage < 100
      ) {
        section.isHidden = true;
      } else if (
        isDirectCampusPathClearForTakeAdmission ||
        natOfficeVisitedCompletionPercentage >= 100 ||
        closureSessionCompletionPercentage >= 100 ||
        section.completionPercentage >= 100
      ) {
        section.isHidden = false;
        section.isDisable = false;
      } else {
        section.isDisable = true;
        if (isNatQualifiedReportPendingFlow) {
          section.isHidden = natSlotSelectionCompletionPercentage < 100;
        }
      }
      if (
        !section.isHidden &&
        !section.isDisable &&
        !selectedSubSectionId &&
        !selectedSectionTabId
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
      }
    } else if (
      section.id === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
    ) {
      enrollmentCompletionPercentage = section.completionPercentage;
      const isSeatReservationCompleted =
        takeAdmissionCompletionPercentage >= 100 ||
        directCampusVisitSrCompletionPercentage >= 100;
      const isEnrollmentAccessible = isDirectFlowUser
        ? natSlotSelectionCompletionPercentage >= 100 &&
        isSeatReservationCompleted
        : isSeatReservationCompleted;

      if (
        natSlotSelectionCompletionPercentage < 100 &&
        !isEnrollmentAccessible
      ) {
        section.isHidden = true;
      } else if (!isEnrollmentAccessible) {
        section.isDisable = true;
        if (isNatQualifiedReportPendingFlow) {
          section.isHidden = false;
        }
      } else {
        section.isHidden = false;
        section.isDisable = false;
      }
      if (
        !section.isHidden &&
        !section.isDisable &&
        !selectedSubSectionId &&
        !selectedSectionTabId &&
        enrollmentCompletionPercentage < 100
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
      }
    } else if (section.id === ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026) {
      if (section.subSection.length > 0) {
        const totalSubCompletion = section.subSection.reduce(
          (sum, sub) => sum + (sub.completionPercentage || 0),
          0
        );
        section.completionPercentage =
          totalSubCompletion / section.subSection.length;
      }

      const isSeatReservationCompleted =
        takeAdmissionCompletionPercentage >= 100 ||
        directCampusVisitSrCompletionPercentage >= 100;
      const isBootcampFlowAccessible = isDirectFlowUser
        ? natSlotSelectionCompletionPercentage >= 100 &&
        isSeatReservationCompleted
        : isSeatReservationCompleted;
      const isEnrollmentStageComplete = isEnrollmentFeeStageComplete(
        sectionDetailsInfoTabsDetailsArray,
        {
          completionPercentage: enrollmentCompletionPercentage
        }
      );

      if (!isBootcampFlowAccessible) {
        section.isHidden = true;
      } else if (!isEnrollmentStageComplete) {
        section.isHidden = true;
        section.isDisable = true;
      } else {
        section.isHidden = false;
        section.isDisable = false;
      }

      if (
        !section.isHidden &&
        !section.isDisable &&
        !selectedSubSectionId &&
        !selectedSectionTabId
      ) {
        selectedSectionTabId = section.id;
        selectedSectionCompletionPercentage = section.completionPercentage;
        selectedSubSectionId = getDefaultBootcampSubSectionId(section);
      }
    }

    sectionDetailsInfoTabsDetailsArray.push(section);
  }

  //hide personal details section if nat section is completed
  if (natSlotSelectionCompletionPercentage >= 100) {
    const personalDetailsSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) => section.id === ApplicationSectionNameEnums.PERSONAL_DETAILS
    );
    const applicationFeeSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
    );
    const directCampusVisitSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_2026
    );
    const directCampusVisitSRSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.DIRECT_CAMPUS_VISIT_SR_2026
    );

    if (personalDetailsSection) {
      personalDetailsSection.isHidden = true;
    }

    if (isSplitSectionsFlow) {
      const activePersonalDetailsSection =
        sectionDetailsInfoTabsDetailsArray.find(
          (section) =>
            section.id === ApplicationSectionNameEnums.PERSONAL_DETAILS_AB1
        );
      const additionalDetailsSection = sectionDetailsInfoTabsDetailsArray.find(
        (section) =>
          section.id === ApplicationSectionNameEnums.STUDENT_ADDITIONAL_DETAILS
      );
      if (activePersonalDetailsSection) {
        activePersonalDetailsSection.isHidden = true;
      }
      if (additionalDetailsSection) {
        additionalDetailsSection.isHidden = true;
      }
    }

    if (applicationFeeSection) {
      applicationFeeSection.isHidden = true;
    }

    if (directCampusVisitSection) {
      directCampusVisitSection.isHidden = true;
    }

    if (directCampusVisitSRSection) {
      directCampusVisitSRSection.isHidden = false;
    }
  }

  const natSlotSelectionSection = sectionDetailsInfoTabsDetailsArray.find(
    (section) => section.id === ApplicationSectionNameEnums.NAT_SLOT_SELECTION
  );

  const shouldApplyPostAdmissionRules =
    natSlotSelectionCompletionPercentage >= 100 &&
    takeAdmissionCompletionPercentage >= 100;

  if (shouldApplyPostAdmissionRules) {
    applySectionVisibilityOverrides({
      hiddenSections: postAdmissionHiddenSections
    });
  }

  if (
    shouldApplyPostAdmissionRules &&
    selectedSectionTabId === ApplicationSectionNameEnums.NAT_SLOT_SELECTION
  ) {
    selectedSectionTabId = '';
    selectedSectionCompletionPercentage = 0;
  }

  if (natSlotSelectionSection?.subSection?.length > 2) {
    natSlotSelectionSection.subSection[2] > 0 &&
      natSlotSelectionSection.subSection[2].completionPercentage < 100 &&
      ((natSlotSelectionSection.subSection[2].completionPercentage = 99),
        (selectedSectionTabId = ApplicationSectionNameEnums.NAT_SLOT_SELECTION));
  }

  const shouldHideSectionOnCompletionOfSR =
    getShouldHideSectionOnCompletionOfSR(sectionDetailsInfoTabsConfig);

  if (shouldHideSectionOnCompletionOfSR) {
    applySectionVisibilityOverrides({
      visibleSections: visibleSectionsOnCompletionOfSR
    });

    const enrollmentSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
    );
    const bootcampSection = sectionDetailsInfoTabsDetailsArray.find(
      (section) => section.id === ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026
    );

    if (
      bootcampSection &&
      isEnrollmentFeeStageComplete(
        sectionDetailsInfoTabsDetailsArray,
        enrollmentSection
      )
    ) {
      bootcampSection.isHidden = false;
      selectedSectionTabId = ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026;
      selectedSectionCompletionPercentage =
        bootcampSection.completionPercentage ?? 0;
      selectedSubSectionId = getDefaultBootcampSubSectionId(bootcampSection);
    } else if (
      enrollmentSection &&
      !isEnrollmentFeeStageComplete(
        sectionDetailsInfoTabsDetailsArray,
        enrollmentSection
      )
    ) {
      enrollmentSection.isHidden = false;
      selectedSectionTabId =
        ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026;
      selectedSectionCompletionPercentage =
        enrollmentSection.completionPercentage ?? 0;
      selectedSubSectionId = '';
    }
  }

  // After seat reservation is complete, land users on NIAT Enrollment Fee next
  // (loop order can leave selection on TAKE_ADMISSION even at 100%).
  const takeAdmissionSectionFinal = sectionDetailsInfoTabsDetailsArray.find(
    (s) => s.id === ApplicationSectionNameEnums.TAKE_ADMISSION
  );
  const enrollmentSectionFinal = sectionDetailsInfoTabsDetailsArray.find(
    (s) => s.id === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
  );
  const bootcampSectionFinal = sectionDetailsInfoTabsDetailsArray.find(
    (s) => s.id === ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026
  );
  const hasBootcampProgress =
    (bootcampSectionFinal?.completionPercentage ?? 0) > 0;
  const isEnrollmentStageComplete = enrollmentSectionFinal
    ? isEnrollmentFeeStageComplete(
      sectionDetailsInfoTabsDetailsArray,
      enrollmentSectionFinal
    )
    : false;
  const isSeatReservationCompleteForEnrollment =
    (takeAdmissionSectionFinal?.completionPercentage ?? 0) >= 100 ||
    directCampusVisitSrCompletionPercentage >= 100;

  if (
    isSeatReservationCompleteForEnrollment &&
    enrollmentSectionFinal &&
    isEnrollmentStageComplete &&
    bootcampSectionFinal &&
    !bootcampSectionFinal.isHidden &&
    !bootcampSectionFinal.isDisable
  ) {
    selectedSectionTabId = ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026;
    selectedSectionCompletionPercentage =
      bootcampSectionFinal.completionPercentage ?? 0;
    selectedSubSectionId = getDefaultBootcampSubSectionId(bootcampSectionFinal);
  } else if (
    (isDirectFlowUser
      ? natSlotSelectionCompletionPercentage >= 100 &&
      isSeatReservationCompleteForEnrollment
      : isSeatReservationCompleteForEnrollment) &&
    enrollmentSectionFinal &&
    !enrollmentSectionFinal.isHidden &&
    !enrollmentSectionFinal.isDisable &&
    !isEnrollmentStageComplete &&
    !hasBootcampProgress
  ) {
    selectedSectionTabId = ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026;
    selectedSectionCompletionPercentage =
      enrollmentSectionFinal.completionPercentage ?? 0;
    selectedSubSectionId = '';
  }

  applyUpskillingAndEnrollmentSidebarVisibility(
    sectionDetailsInfoTabsDetailsArray
  );

  return {
    sectionDetailsInfoTabsDetailsArray,
    selectedSectionTabId,
    selectedSubSectionId,
    selectedSectionCompletionPercentage
  };
};

export const getSectionDetailsInfoTabsConfig = (
  sectionDetails: any,
  loggedInUserRole: string,
  applicationType?: string,
  templateApplicationType?: string,
  isDirectCampusVisitedFromField = false,
  userProfileDetailsFlow?: string
): any => {
  const sectionDetailsInfoTabsConfig: any[] = [];
  sectionDetails?.forEach((eachSection) => {
    const sectionDetailsInfoTabsConfigItem = {
      title: eachSection.title,
      description: eachSection.description,
      id: eachSection.sectionNameEnum,
      isSelected: false,
      completionPercentage: eachSection.completionPercentage,
      isDisable: true,
      sectionType: eachSection.sectionType,
      subSection: [],
      isHidden: eachSection.isHidden,
      shouldShowSubSection: false,
      sectionCompletedAt: eachSection.sectionCompletedAt,
      firstVisitedDatetime: eachSection.firstVisitedDatetime ?? null,
      extraConfig: eachSection.extraConfig,
      programConsentConfirmationExtraConfig:
        eachSection.programConsentConfirmationExtraConfig ?? null,
      hasProgramConsentConfirmationEntity:
        eachSection.hasProgramConsentConfirmationEntity ?? false,
      sectionId: eachSection.sectionId
    };

    if (eachSection.parentSectionDetails) {
      const parentNameEnum = eachSection.parentSectionDetails.nameEnum;
      const parentSection = sectionDetailsInfoTabsConfig.find(
        (section) => section.id === parentNameEnum
      );

      if (parentSection) {
        parentSection.subSection.push(sectionDetailsInfoTabsConfigItem);
      } else {
        sectionDetailsInfoTabsConfig.push({
          title: eachSection.parentSectionDetails.title,
          description: eachSection.parentSectionDetails.description,
          id: parentNameEnum,
          isSelected: false,
          completionPercentage: 0,
          sectionType: eachSection.sectionType,
          isDisable: true,
          subSection: [sectionDetailsInfoTabsConfigItem],
          isHidden: eachSection.isHidden,
          shouldShowSubSection: false,
          sectionCompletedAt: eachSection.sectionCompletedAt,
          firstVisitedDatetime: eachSection.firstVisitedDatetime ?? null,
          extraConfig: eachSection.extraConfig,
          sectionId: eachSection.sectionId
        });
      }
    } else {
      sectionDetailsInfoTabsConfig.push(sectionDetailsInfoTabsConfigItem);
    }
  });
  return getSectionDetailsDisableInfoTabsConfig(
    sectionDetailsInfoTabsConfig,
    loggedInUserRole,
    undefined,
    applicationType,
    templateApplicationType,
    isDirectCampusVisitedFromField,
    userProfileDetailsFlow
  );
};
const APAndNonAPSectionTabIds = [
  ApplicationSectionNameEnums.WEBINAR,
  ApplicationSectionNameEnums.ONLINE_COUNSELLING,
  ApplicationSectionNameEnums.CLOSURE_SESSION_REGISTRATION,

  ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT,

  ApplicationSectionNameEnums.TAKE_ADMISSION
];

const isAPFlow = (sectionDetailsInfoTabsConfig: any[]): boolean => {
  return sectionDetailsInfoTabsConfig.some((section) => {
    return (
      section.id === ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT
    );
  });
};
export const getSectionQuotationText = ({ niatApplicationMachineState }) => {
  if (niatApplicationMachineState) {
    const {
      context: {
        currentSelectedSectionTabId,
        sectionDetailsInfoTabsConfig,
        currentSelectedSubSectionId,
        currentSectionType
      }
    } = niatApplicationMachineState;
    const applicationFeeSection = sectionDetailsInfoTabsConfig.find(
      (section) =>
        section.id === ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
    );
    const activeSectionId =
      currentSelectedSubSectionId || currentSelectedSectionTabId;

    if (
      activeSectionId === ApplicationSectionNameEnums.NIAT_ENROLLMENT_FEE_2026
    ) {
      return ENROLLMENT_FEE_QUOTATION_BANNER_TEXT;
    }

    if (
      activeSectionId === ApplicationSectionNameEnums.NIAT_BOOTCAMP_2026 ||
      activeSectionId ===
      ApplicationSectionNameEnums.NIAT_BOOTCAMP_REGISTRATION_2026 ||
      activeSectionId === ApplicationSectionNameEnums.NIAT_BOOTCAMP_FEE_2026
    ) {
      return BOOTCAMP_QUOTATION_BANNER_TEXT;
    }

    if (
      currentSelectedSectionTabId ===
      ApplicationSectionNameEnums.PERSONAL_DETAILS ||
      currentSelectedSectionTabId ===
      ApplicationSectionNameEnums.PERSONAL_DETAILS_AB1 ||
      currentSelectedSectionTabId ===
      ApplicationSectionNameEnums.STUDENT_ADDITIONAL_DETAILS
    ) {
      return `<span>🥳 2,36,000+ students</span> like you across India took this step. Join them today!`;
    } else if (
      currentSelectedSectionTabId ===
      ApplicationSectionNameEnums.BOOK_ADMISSION_TEST_FEE
    ) {
      return `A <strong>90-Minute Test</strong> To Uncover Your True Potential!`;
    } else if (
      currentSelectedSectionTabId ===
      ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER
    ) {
      return `🥳 See and feel the <span>NIAT's ecosystem.</span>`;
    } else if (
      applicationFeeSection?.completionPercentage >= 100 &&
      currentSelectedSubSectionId ===
      ApplicationSectionNameEnums.EXAM_SLOT_DETAILS
    ) {
      return `🔥 Slots filling fast! Book your preferred date and time for the test slot now.`;
    } else if (
      currentSelectedSubSectionId === ApplicationSectionNameEnums.TOPIN_EXAM
    ) {
      if (
        currentSectionType === ApplicationSectionNameEnums.EXAM_SLOT_SUCCESS ||
        currentSectionType ===
        ApplicationSectionNameEnums.RESCHEDULE_SLOT_SUCCESS
      ) {
        return `Don't miss giving your NAT! Download the <strong><a id="nat-guide-link-nudge" href="${NAT_GUIDE_URL}" target="_blank">NAT Guide</a></strong> for more insights.`;
      } else if (
        currentSectionType === ApplicationSectionNameEnums.RESCHEDULE_SLOT
      ) {
        return `Reschedule Now! Take the NAT at your convenience.`;
      } else {
        return `NAT report helps you understand where you stand in the core abilities needed for a tech career.`;
      }
    } else if (
      applicationFeeSection?.completionPercentage >= 100 &&
      currentSelectedSubSectionId ===
      ApplicationSectionNameEnums.RETAKE_EXAM_SLOT_DETAILS
    ) {
      return `Success might just be one attempt away. Reapply for NAT now!`;
    } else if (
      currentSelectedSubSectionId ===
      ApplicationSectionNameEnums.RETAKE_TOPIN_EXAM
    ) {
      if (
        currentSectionType ===
        ApplicationSectionNameEnums.RETAKE_RESCHEDULE_SLOT
      ) {
        return `Reschedule Now! Take the NAT at your convenience.`;
      } else if (
        currentSectionType ===
        ApplicationSectionNameEnums.RETAKE_RESCHEDULE_SLOT_SUCCESS ||
        currentSectionType ===
        ApplicationSectionNameEnums.RETAKE_EXAM_SLOT_SUCCESS
      ) {
        return `Don't miss giving your NAT! Download the <strong><a id="nat-guide-link-nudge" href="${NAT_GUIDE_URL}" target="_blank">NAT Guide</a></strong> for more insights.`;
      } else {
        return `NAT report helps you understand where you stand in the core abilities needed for a tech career.`;
      }
    } else if (APAndNonAPSectionTabIds.includes(currentSelectedSectionTabId)) {
      for (const section of sectionDetailsInfoTabsConfig) {
        if (section.completionPercentage < 100) {
          if (
            section.id ===
            ApplicationSectionNameEnums.CAMPUS_VISIT_SCHEDULER_PARENT
          ) {
            return `Schedule your <span>Experience Center visit</span> to complete the in-person evaluation process.`;
          } else if (
            section.id === ApplicationSectionNameEnums.ONLINE_COUNSELLING
          ) {
            const onlineCounsellingNudgeText1 = `Book online counseling to discuss your results and personalized learning path.`;
            const onlineCounsellingNudgeText2 = `Online counseling helps you clarify course options, eligibility, and next actions.`;
            return section.completionPercentage < 100 &&
              section.completionPercentage >= 50
              ? onlineCounsellingNudgeText2
              : onlineCounsellingNudgeText1;
          } else if (section.id === ApplicationSectionNameEnums.WEBINAR) {
            const liveSessionNudgeText1 = `Register for the <span>live session</span> to understand the NIAT process and next steps clearly.`;
            const liveSessionNudgeText2 = `Join the <span>live session</span> to get guidance on counselling and seat reservation.`;
            return section.completionPercentage < 100 &&
              section.completionPercentage >= 50
              ? liveSessionNudgeText2
              : liveSessionNudgeText1;
          }
        }
      }
    }
  }
  return `🎯 The journey to becoming a <span>Future Tech Leader</span> starts here!`;
};
